'use client';
import SafeImage from '@ui/SafeImage';
import { prefix } from '@util/imagePrefix';
import { cn } from '@util/index';
import { ArrowRightIcon, CheckIcon, XIcon } from 'lucide-react';
import type { UserBike } from 'models/shared';

type FitmentVehicleCardVariant = 'list' | 'trigger';

interface FitmentVehicleCardProps {
  bike: Pick<Partial<UserBike>, 'make' | 'model' | 'year'>;
  variant?: FitmentVehicleCardVariant;
  isSelected?: boolean;
  showSelectedCheck?: boolean;
  onClick?: () => void;
  onDeselect?: () => void;
  onToggleSelected?: () => void;
  /** Offered in place of picking another vehicle, for one not in the garage. */
  onSave?: () => void;
  className?: string;
}

export const FitmentVehicleCard = ({
  bike,
  variant = 'list',
  isSelected = false,
  showSelectedCheck = true,
  onClick,
  onDeselect,
  onToggleSelected,
  onSave,
  className,
}: FitmentVehicleCardProps) => {
  const parsedBrandName =
    bike.make === 'Gas Gas'
      ? bike.make.split(' ').join('').toLowerCase()
      : bike.make?.split(' ').join('-').toLowerCase();
  const brandImage = `${prefix}/assets/brands/${parsedBrandName}_1920x2560.png`;

  if (variant === 'trigger') {
    return (
      <div className={cn('flex w-full flex-col gap-y-3', className)}>
        <div className="flex w-full items-center justify-between gap-x-6 rounded-[2rem] border border-brand-secondary bg-white px-8 py-3 text-left shadow-sm">
          <div className="flex items-center gap-6">
            {!!onToggleSelected && (
              <button
                type="button"
                role="checkbox"
                aria-checked={isSelected}
                aria-label={
                  isSelected
                    ? 'Stop filtering results by this vehicle'
                    : 'Filter results by this vehicle'
                }
                title={
                  isSelected
                    ? 'Results are filtered for this vehicle'
                    : 'Filter results for this vehicle'
                }
                onClick={onToggleSelected}
                className={cn(
                  'flex h-10 w-10 shrink-0 items-center justify-center rounded-full border-2 transition-colors',
                  isSelected
                    ? 'border-brand-secondary bg-brand-secondary'
                    : 'border-zinc-300 bg-white hover:border-zinc-500'
                )}
              >
                {isSelected && (
                  <CheckIcon className="h-6 w-6 text-white" strokeWidth={3} />
                )}
              </button>
            )}

            <SafeImage
              alt={bike.make ?? ''}
              src={brandImage}
              fallback={
                <div className="flex h-16 w-20 items-center justify-center rounded-[0.5rem] bg-zinc-200 p-1 text-center text-[1rem] font-medium text-zinc-600">
                  {bike.make}
                </div>
              }
              className="h-16 w-28 shrink-0 object-contain"
            />

            <div className="flex flex-col">
              <span className="text-[1.8rem] font-medium text-black">
                {bike.make}
              </span>

              <span className="max-w-[20rem] truncate text-[1.6rem] font-medium text-zinc-700">
                {bike.year} {bike.model}
              </span>
            </div>
          </div>

          <button
            type="button"
            onClick={onDeselect}
            aria-label="Remove vehicle"
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-zinc-500 transition-colors hover:bg-zinc-100 hover:text-zinc-800"
          >
            <XIcon className="h-8 w-8" />
          </button>
        </div>

        <button
          type="button"
          onClick={onSave ?? onClick}
          className="flex items-center gap-x-2 self-start text-[1.6rem] font-semibold text-brand-secondary transition-colors hover:text-brand-secondary-darker"
        >
          {onSave ? 'Save to garage' : 'Select another vehicle'}
          <ArrowRightIcon className="h-6 w-6" />
        </button>
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'flex w-full items-center gap-x-4 rounded-[1.5rem] p-4 text-left transition-colors',
        isSelected
          ? 'border-2 border-red-500 bg-red-50 hover:bg-red-100'
          : 'hover:opacity-80',
        className
      )}
    >
      <SafeImage
        alt={bike.make ?? ''}
        src={brandImage}
        fallback={
          <div className="flex h-36 w-36 items-center justify-center rounded-[0.5rem] bg-zinc-200 text-center text-[1rem] font-medium text-zinc-600">
            {bike.make}
          </div>
        }
        height={144}
        width={144}
        className="h-36 w-36 shrink-0 object-contain"
      />

      <div className="flex flex-1 flex-col">
        <span className="text-[1.6rem] font-semibold leading-tight text-black">
          {bike.year} {bike.make} {bike.model}
        </span>
      </div>

      {isSelected && showSelectedCheck && (
        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-red-500">
          <CheckIcon className="h-5 w-5 text-white" />
        </div>
      )}
    </button>
  );
};

export default FitmentVehicleCard;
